'use client'

import { useState, Suspense } from 'react'
import { Logo } from '@/components/logo'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AuthBackground } from '@/components/ui/auth-background'
import { useAuthForm } from '@/hooks/use-auth-form'
import { Loader2, Eye, EyeOff } from 'lucide-react'
import Link from 'next/link'
import { DotPattern } from '@/components/magicui/dot-pattern'
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/animate-ui/radix/tooltip'

function SignUpForm() {
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const { loading, handleSignup, handleGoogleSignIn } = useAuthForm()

    const handleGoogleSignUp = () => {
        // Better Auth social sign-in (Convex-backed)
        void handleGoogleSignIn()
    }

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (password !== confirmPassword) {
            alert('Passwords do not match')
            return
        }

        const fullName = `${firstName} ${lastName}`.trim()
        await handleSignup(email, password, fullName)
    }

    return (
        <>
            {/* Simplified Dot Pattern Background - No glow for better performance */}
            <DotPattern
                width={24}
                height={24}
                cx={1}
                cy={1}
                cr={1}
                glow={false}
                className="fill-neutral-400/15 [mask-image:radial-gradient(800px_circle_at_center,white,transparent)]"
            />

            <AuthBackground
                spotlightIntensity="subtle"
                focusPosition="center"
                animationSpeed="slow"
                colorScheme="cyan-green"
                enableFormFocus={true}
                enableAccentGradients={false}
            />

            <section className="relative min-h-screen bg-white flex will-change-transform">
                {/* Left Side - Branding & Features - Hidden on mobile */}
                <div className="hidden lg:flex flex-1 lg:flex-[2] p-8 lg:p-16 flex-col justify-center relative overflow-hidden">
                    {/* Floating Audience Tag (doesn't affect layout) */}
                    <div className="absolute top-8 left-8 z-10 flex items-center gap-3">
                        <div className="bg-[#facc15] border-4 border-black px-3 py-1 transform rotate-1">
                            <span className="font-black text-black text-xs tracking-widest">FOR EVENT HOSTS</span>
                        </div>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        type="button"
                                        className="bg-black text-white border-4 border-black px-3 py-1 text-xs font-black transform rotate-1 hover:rotate-0"
                                    >
                                        Not a host?
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="max-w-xs">
                                    <div className="space-y-2">
                                        <p className="font-bold">Want to try the guest experience?</p>
                                        <Link href="/guest-static/" className="inline-block bg-white text-black border-2 border-black px-2 py-1 font-black">
                                            Open Guest Demo
                                        </Link>
                                    </div>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                    {/* Logo Section */}
                    <div className="mb-12">
                        <Link href="/" aria-label="go home" className="inline-block mb-8">
                            <div className="bg-black border-8 border-black p-6 inline-block transform -rotate-2 hover:rotate-0 transition-transform duration-200">
                                <Logo height={60} uniColor />
                            </div>
                        </Link>
                        <h1 className="text-6xl font-black text-black mb-6 leading-tight">
                            CREATE YOUR<br />
                            <span className="text-cyan-500">EVENT HOST ACCOUNT</span>
                        </h1>
                        <p className="text-2xl font-bold text-black mb-8">
                            SET UP YOUR PROFILE • START HOSTING • GROW YOUR COMMUNITY
                        </p>
                    </div>

                    {/* Features Grid */}
                    <div className="grid grid-cols-2 gap-6 mb-12">
                        <div className="bg-cyan-400 border-4 border-black p-6 text-center">
                            <div className="text-2xl font-black text-black mb-1">EASY SETUP</div>
                            <div className="font-bold text-black text-sm">Launch an event in minutes</div>
                        </div>
                        <div className="bg-green-400 border-4 border-black p-6 text-center">
                            <div className="text-2xl font-black text-black mb-1">INTERACTIVE GAMES</div>
                            <div className="font-bold text-black text-sm">Icebreakers that get people talking</div>
                        </div>
                        <div className="bg-pink-400 border-4 border-black p-6 text-center">
                            <div className="text-2xl font-black text-black mb-1">SMART MATCHMAKING</div>
                            <div className="font-bold text-black text-sm">Connect by goals, roles, interests</div>
                        </div>
                        <div className="bg-yellow-400 border-4 border-black p-6 text-center">
                            <div className="text-2xl font-black text-black mb-1">SECURE & PRIVATE</div>
                            <div className="font-bold text-black text-sm">Control access and protect data</div>
                        </div>
                    </div>

                    {/* Call to Action */}
                    <div className="bg-purple-400 border-8 border-black p-8">
                        <h3 className="text-2xl font-black text-black mb-4">READY TO HOST?</h3>
                        <p className="text-lg font-bold text-black mb-4">
                            Create events, invite attendees, and run interactive sessions.
                        </p>
                        <div className="flex items-center space-x-2">
                            <div className="bg-black text-white px-4 py-2 font-black">→</div>
                            <span className="font-black text-black">SIGN UP NOW!</span>
                        </div>
                    </div>
                </div>

                {/* Right Side - Signup Form - Full width on mobile */}
                <div className="w-full lg:w-96 xl:w-[32rem] bg-gray-50 lg:border-l-8 border-black p-4 sm:p-8 flex flex-col justify-center min-h-screen lg:min-h-0">
                    {/* Mobile Header - Only visible on mobile */}
                    <div className="lg:hidden mb-8 space-y-4">
                        {/* Mobile Tags - Responsive Layout */}
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3">
                            <div className="bg-[#facc15] border-2 sm:border-4 border-black px-2 sm:px-3 py-1 transform rotate-1">
                                <span className="font-black text-black text-xs sm:text-sm tracking-widest">FOR EVENT HOSTS</span>
                            </div>
                            {/* Mobile: Direct link, Desktop: Tooltip */}
                            <div className="relative group">
                                {/* Mobile - Direct link */}
                                <Link 
                                    href="/guest-static/" 
                                    className="sm:hidden bg-black text-white border-2 border-black px-2 py-1 text-xs font-black transform rotate-1 hover:rotate-0 transition-transform inline-block"
                                >
                                    Not a host?
                                </Link>
                                
                                {/* Desktop - Tooltip */}
                                <div className="hidden sm:block">
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <button type="button" className="bg-black text-white border-2 sm:border-4 border-black px-2 py-1 text-xs sm:text-sm font-black transform rotate-1 hover:rotate-0 transition-transform">
                                                    Not a host?
                                                </button>
                                            </TooltipTrigger>
                                            <TooltipContent side="bottom" className="max-w-xs">
                                                <div className="space-y-2">
                                                    <p className="font-bold">Want to try the guest experience?</p>
                                                    <Link href="/guest-static/" className="inline-block bg-white text-black border-2 border-black px-2 py-1 font-black text-xs">
                                                        Open Guest Demo
                                                    </Link>
                                                </div>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                </div>
                            </div>
                        </div>
                        
                        {/* Mobile Logo */}
                        <div className="text-center">
                            <Link href="/" aria-label="go home" className="inline-block">
                                <div className="bg-black border-2 sm:border-4 border-black p-3 sm:p-4 inline-block transform rotate-1 hover:rotate-0 transition-transform duration-200">
                                    <Logo className="h-8 sm:h-10 w-auto text-white" uniColor />
                                </div>
                            </Link>
                            <p className="mt-3 text-base sm:text-lg font-bold text-black">HOST • ENGAGE • GROW</p>
                        </div>
                    </div>
                    {/* Header */}
                    <div className="bg-green-400 border-4 lg:border-8 border-black p-4 lg:p-6 mb-6 lg:mb-8">
                        <h2 className="text-2xl lg:text-3xl font-black text-black mb-2">CREATE YOUR HOST ACCOUNT</h2>
                        <p className="text-base lg:text-lg font-bold text-black">KICKSTART YOUR NEXT EVENT IN MINUTES</p>
                    </div>

                    {/* Social Signup Section */}
                    <div className="bg-pink-400 border-4 border-black p-4 lg:p-6 transform -rotate-1 hover:rotate-0 transition-transform duration-200 mb-4 lg:mb-6">
                        <h3 className="text-base lg:text-lg font-black text-black mb-3 lg:mb-4">QUICK SIGNUP</h3>
                        <div className="space-y-3">
                            <Button
                                type="button"
                                onClick={handleGoogleSignUp}
                                disabled={loading}
                                className="w-full bg-white border-4 border-black text-black font-black hover:bg-black hover:text-white transition-colors duration-150 transform hover:rotate-1"
                            >
                                {loading ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="18"
                                        height="18"
                                        viewBox="0 0 256 262"
                                        className="mr-2"
                                    >
                                        <path
                                            fill="#4285f4"
                                            d="M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.690 42.356l-.244 1.622l38.755 30.023l2.685.268c24.659-22.774 38.875-56.282 38.875-96.027"></path>
                                        <path
                                            fill="#34a853"
                                            d="M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055c-34.523 0-63.824-22.773-74.269-54.25l-1.531.13l-40.298 31.187l-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1"></path>
                                        <path
                                            fill="#fbbc05"
                                            d="M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82c0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602z"></path>
                                        <path
                                            fill="#eb4335"
                                            d="M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0C79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251"></path>
                                    </svg>
                                )}
                                CONTINUE WITH GOOGLE
                            </Button>
                            <Button
                                type="button"
                                className="w-full bg-white border-4 border-black text-black font-black hover:bg-black hover:text-white transition-colors duration-150 transform hover:-rotate-1"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="18"
                                    viewBox="0 0 256 256"
                                    className="mr-2"
                                >
                                    <path fill="#f1511b" d="M121.666 121.666H0V0h121.666z"></path>
                                    <path fill="#80cc28" d="M256 121.666H134.335V0H256z"></path>
                                    <path fill="#00adef" d="M121.663 256.002H0V134.336h121.663z"></path>
                                    <path fill="#fbbc09" d="M256 256.002H134.335V134.336H256z"></path>
                                </svg>
                                CONTINUE WITH MICROSOFT
                            </Button>
                        </div>
                    </div>

                    {/* OR Divider */}
                    <div className="mb-4 lg:mb-6">
                        <div className="relative flex items-center">
                            <div className="w-full border-t-4 border-black"></div>
                        </div>
                        <div className="relative flex justify-center">
                            <div className="bg-white px-4 font-black text-black">OR</div>
                        </div>
                    </div>

                    {/* Main Signup Form */}
                    <form
                        onSubmit={onSubmit}
                        className="bg-blue-400 border-4 border-black p-4 lg:p-6 transform rotate-1 hover:rotate-0 transition-transform duration-200 mb-4 lg:mb-6 will-change-transform"
                    >
                        <div className="space-y-4 lg:space-y-6">
                            {/* Name Fields */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
                                <div>
                                    <Label htmlFor="firstName" className="block text-base lg:text-lg font-black text-black mb-2">
                                        FIRST NAME
                                    </Label>
                                    <Input
                                        type="text"
                                        required
                                        name="firstName"
                                        id="firstName"
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                        disabled={loading}
                                        placeholder="PETER"
                                        className="border-4 border-black bg-white font-bold text-black placeholder:text-black/60 placeholder:font-bold focus:bg-yellow-400 focus:border-6 transition-colors duration-150 text-base lg:text-lg p-2 lg:p-3 will-change-auto"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="lastName" className="block text-base lg:text-lg font-black text-black mb-2">
                                        LAST NAME
                                    </Label>
                                    <Input
                                        type="text"
                                        required
                                        name="lastName"
                                        id="lastName"
                                        value={lastName}
                                        onChange={(e) => setLastName(e.target.value)}
                                        disabled={loading}
                                        placeholder="PARKER"
                                        className="border-4 border-black bg-white font-bold text-black placeholder:text-black/60 placeholder:font-bold focus:bg-yellow-400 focus:border-6 transition-colors duration-150 text-base lg:text-lg p-2 lg:p-3 will-change-auto"
                                    />
                                </div>
                            </div>

                            {/* Email Field */}
                            <div>
                                <Label htmlFor="email" className="block text-base lg:text-lg font-black text-black mb-2">
                                    EMAIL ADDRESS
                                </Label>
                                <Input
                                    type="email"
                                    required
                                    name="email"
                                    id="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={loading}
                                    placeholder="peterparker@spiderman.com"
                                    className="border-4 border-black bg-white font-bold text-black placeholder:text-black/60 placeholder:font-bold focus:bg-yellow-400 focus:border-6 transition-colors duration-150 text-base lg:text-lg p-2 lg:p-3 will-change-auto"
                                />
                            </div>

                            {/* Password Field */}
                            <div>
                                <Label htmlFor="password" className="block text-base lg:text-lg font-black text-black mb-2">
                                    PASSWORD
                                </Label>
                                <div className="relative">
                                    <Input
                                        type={showPassword ? "text" : "password"}
                                        required
                                        name="password"
                                        id="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        disabled={loading}
                                        placeholder="create strong password"
                                        className="border-4 border-black bg-white font-bold text-black placeholder:text-black/60 placeholder:font-bold focus:bg-yellow-400 focus:border-6 transition-colors duration-150 text-base lg:text-lg p-2 lg:p-3 pr-10 lg:pr-12 will-change-auto"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-black hover:text-gray-600 transition-colors"
                                    >
                                        {showPassword ? <EyeOff className="w-4 h-4 lg:w-5 lg:h-5" /> : <Eye className="w-4 h-4 lg:w-5 lg:h-5" />}
                                    </button>
                                </div>
                            </div>

                            {/* Confirm Password Field */}
                            <div>
                                <Label htmlFor="confirmPassword" className="block text-base lg:text-lg font-black text-black mb-2">
                                    CONFIRM PASSWORD
                                </Label>
                                <div className="relative">
                                    <Input
                                        type={showConfirmPassword ? "text" : "password"}
                                        required
                                        name="confirmPassword"
                                        id="confirmPassword"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        disabled={loading}
                                        placeholder="repeat your password"
                                        className="border-4 border-black bg-white font-bold text-black placeholder:text-black/60 placeholder:font-bold focus:bg-yellow-400 focus:border-6 transition-colors duration-150 text-base lg:text-lg p-2 lg:p-3 pr-10 lg:pr-12 will-change-auto"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-black hover:text-gray-600 transition-colors"
                                    >
                                        {showConfirmPassword ? <EyeOff className="w-4 h-4 lg:w-5 lg:h-5" /> : <Eye className="w-4 h-4 lg:w-5 lg:h-5" />}
                                    </button>
                                </div>
                            </div>

                            {/* Submit Button */}
                            <Button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-black text-white font-black text-base lg:text-lg p-2 lg:p-3 border-4 border-black hover:bg-white hover:text-black transition-colors duration-150 transform hover:rotate-1 group will-change-transform"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 lg:h-5 lg:w-5 animate-spin" />
                                        CREATING ACCOUNT...
                                    </>
                                ) : (
                                    "CREATE ACCOUNT NOW!"
                                )}
                            </Button>
                        </div>
                    </form>

                    {/* Sign In Link */}
                    <div className="bg-[#facc15] border-4 border-black p-3 lg:p-4 text-center transform -rotate-1 hover:rotate-0 transition-transform duration-200">
                        <p className="text-base lg:text-lg font-black text-black mb-2">
                            ALREADY HAVE AN ACCOUNT?
                        </p>
                        <Link
                            href="/login"
                            className="bg-black text-white px-4 lg:px-6 py-2 inline-block border-4 border-black hover:bg-white hover:text-black transition-all duration-200 transform rotate-1 hover:rotate-0 font-black text-sm lg:text-base"
                        >
                            SIGN IN
                        </Link>
                    </div>
                </div>
            </section>
        </>
    )
}

export default function SignUpPage() {
    return (
        <Suspense fallback={<div>Loading...</div>}>
            <SignUpForm />
        </Suspense>
    )
}
